import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { get } from "lodash";
import { Task } from "protogen/tasks_pb";
import { Family } from "protogen/advisors_service_pb";
import TasksTable from "components/tasks/TasksTable";
import { AddFilterContext } from "components/tasks/FilterContext";
import { useListTasksForAdvisor, useListTasksForFamily } from "services/tasks";
import { TaskFilter } from "components/tasks/FilterContext";
import { buildRequest, notDoneStages } from "components/tasks/utils";
import Loading from "components/common/Loading";
import { PanelProps } from "components/details-page/TabbedDetailsPanel";
import SearchBar from "components/common/SearchBar";
import useIsMobile from "components/hooks/useIsMobile";

const _Wrapped = ({
  family,
  initialTasks,
}: {
  family: Family;
  initialTasks: Task[];
}) => {
  const [tasks, setTasks] = useState<Task[]>(initialTasks);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const onAddTaskClick = () => {
    navigate("/tasks/add", { state: { family } });
  };
  const { loading, request } = useListTasksForAdvisor((r) => {
    setTasks(r.tasks);
  });
  const fetchTasks = (filters: TaskFilter[]) => {
    request(buildRequest(filters.filter((f) => f.field !== "status")));
  };

  let filteredTasks = tasks;
  if (query) {
    const queryPaths = ["title", "priority", "state"];
    const regexPattern = new RegExp(
      query
        .split(" ")
        .map((sub) => `(${sub})`)
        .join("|"),
      "i",
    );

    filteredTasks = filteredTasks.filter((t) => {
      const searchableValues = queryPaths.map((path) => get(t, path)).join(" ");
      return regexPattern.test(searchableValues);
    });
  }

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3">Tasks</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            gap: "8px",
          }}
        >
          <Button onClick={onAddTaskClick}>Add task</Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          justifyContent: "space-between",
          gap: "8px",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <SearchBar
            fullWidth={isMobile}
            onQuery={setQuery}
            onKeyPress={true}
            sx={{ marginLeft: "0px" }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "16px",
          alignSelf: "stretch",
          flexDirection: "column",
        }}
      >
        <TasksTable
          tasks={filteredTasks}
          loading={loading}
          fetchTasks={fetchTasks}
          families={[{ label: family.name, value: family.ref }]}
          showFamilyFilter={false}
          showProjectsFilter={true}
          familyRef={family.ref}
          listName={`${family.name} tasks`}
          listPath={`/families/${encodeURIComponent(family.ref)}#tab-tasks`}
        />
      </Box>
    </Box>
  );
};

interface Props extends PanelProps {
  title: string;
  family: Family;
}

export default ({ family }: Props) => {
  const initialFamilies = [family.ref];
  const { data, request: listTasksRequest } = useListTasksForFamily();
  useEffect(() => {
    const getTasks = async () => {
      await listTasksRequest({
        familyRef: family.ref,
        taskStates: [],
        taskStages: notDoneStages,
      });
    };
    getTasks();
  }, [family.ref]);
  if (!data) {
    return <Loading />;
  }
  return (
    <AddFilterContext
      initialStages={[]}
      initialFamilies={initialFamilies}
      initialPriorities={[]}
    >
      <_Wrapped family={family} initialTasks={data.tasks} />
    </AddFilterContext>
  );
};
